import { Box, CircularProgress, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { useTranslation } from "../../localization/useTranslation";
import { AiChatAttachment, AiChatAttachmentStatus } from "../AiChat.types";
import { DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";

interface AiChatAttachmentChipsProps {
    attachments: AiChatAttachment[];
    pendingDeleteIds: Set<string>;
    onPreview: (attachment: AiChatAttachment) => void;
    onDelete: (attachmentId: string) => void;
}

function AiChatAttachmentChips({ attachments, pendingDeleteIds, onPreview, onDelete }: AiChatAttachmentChipsProps) {
    const theme = useTheme();
    const { translateString } = useTranslation();

    if (attachments.length === 0) return null;

    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, pb: 1 }}>
            {attachments.map((attachment) => (
                <AttachmentChip
                    key={attachment.id}
                    attachment={attachment}
                    isDeleting={pendingDeleteIds.has(attachment.id)}
                    onPreview={onPreview}
                    onDelete={onDelete}
                    failedTooltip={translateString("aiChatAttachmentFailed")}
                    processingTooltip={translateString("aiChatAttachmentProcessing")}
                    readyTooltip={translateString("aiChatAttachmentReady")}
                    removeAriaLabel={translateString("aiChatRemoveAttachment")}
                    chipBg={theme.palette.grey[100]}
                    chipBorder={theme.palette.divider}
                    chipText={theme.palette.text.primary}
                    chipMuted={theme.palette.text.secondary}
                    chipDanger={theme.palette.error.main}
                    chipPrimary={theme.palette.primary.main}
                />
            ))}
        </Box>
    );
}

interface AttachmentChipProps {
    attachment: AiChatAttachment;
    isDeleting: boolean;
    onPreview: (attachment: AiChatAttachment) => void;
    onDelete: (attachmentId: string) => void;
    failedTooltip: string;
    processingTooltip: string;
    readyTooltip: string;
    removeAriaLabel: string;
    chipBg: string;
    chipBorder: string;
    chipText: string;
    chipMuted: string;
    chipDanger: string;
    chipPrimary: string;
}

function AttachmentChip({
    attachment,
    isDeleting,
    onPreview,
    onDelete,
    failedTooltip,
    processingTooltip,
    readyTooltip,
    removeAriaLabel,
    chipBg,
    chipBorder,
    chipText,
    chipMuted,
    chipDanger,
    chipPrimary,
}: AttachmentChipProps) {
    const isReady = attachment.status === AiChatAttachmentStatus.Ready;
    const isFailed = attachment.status === AiChatAttachmentStatus.Failed;
    const tooltip = isFailed ? failedTooltip : isReady ? readyTooltip : processingTooltip;

    function renderStatusIcon() {
        if (attachment.status === AiChatAttachmentStatus.Processing) {
            return <CircularProgress size={14} thickness={5} sx={{ color: chipMuted }} />;
        }
        if (isFailed) {
            return <ErrorOutlineIcon sx={{ fontSize: 16, color: chipDanger }} />;
        }
        return <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: chipPrimary }} />;
    }

    return (
        <Tooltip title={tooltip} arrow>
            <Box
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    paddingLeft: 1,
                    paddingRight: 0.25,
                    paddingTop: 0.25,
                    paddingBottom: 0.25,
                    borderRadius: 2,
                    border: `1px solid ${isFailed ? chipDanger : chipBorder}`,
                    backgroundColor: chipBg,
                    maxWidth: 240,
                }}
            >
                <Box sx={{ width: 18, height: 18, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {renderStatusIcon()}
                </Box>
                <Typography
                    component={isReady ? "button" : "span"}
                    onClick={isReady ? () => onPreview(attachment) : undefined}
                    sx={{
                        fontSize: 12,
                        color: chipText,
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: isReady ? "pointer" : "default",
                        textAlign: "left",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: 160,
                        "&:hover": isReady ? { textDecoration: "underline" } : undefined,
                    }}
                >
                    {attachment.fileName}
                </Typography>
                <Typography sx={{ fontSize: 11, color: chipMuted, whiteSpace: "nowrap" }}>{formatFileSize(attachment.fileSize)}</Typography>
                {isDeleting ? (
                    <Box sx={{ width: 18, height: 18, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <CircularProgress size={14} thickness={5} sx={{ color: chipMuted }} />
                    </Box>
                ) : (
                    <DotLegalTooltip text={removeAriaLabel}>
                        <IconButton size="small" onClick={() => onDelete(attachment.id)} sx={{ padding: 0, width: 18, height: 18, flexShrink: 0 }}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </DotLegalTooltip>
                )}
            </Box>
        </Tooltip>
    );
}

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default AiChatAttachmentChips;
